// One continuous 89.6-second railway suite. Two media decks overlap the final
// 2.4 seconds so MP3 encoder padding never becomes an audible browser gap.

const SUITE_SECONDS = 89.6;
const CROSSFADE_SECONDS = 2.4;
const CROSSFADE_AT = SUITE_SECONDS - CROSSFADE_SECONDS;
const WHISTLE_COOLDOWN_MS = 12_000;

const SCENE_VOLUME = {
  hero: 0.78,
  timetable: 0.66,
  tunnel: 0.72,
  railway: 0.82,
  finale: 0.86,
};

const FILES = {
  suite: '/assets/audio/thomas-railway-suite.mp3',
  whistleShort: '/assets/audio/whistle-short.mp3',
  whistleStrong: '/assets/audio/whistle-strong.mp3',
  whistleDistant: '/assets/audio/whistle-distant.mp3',
};

let decks;
let deckGain = [1, 0];
let activeDeckIndex = 0;
let active = false;
let suspended = false;
let starting = null;
let currentScene = 'hero';
let sceneVolume = SCENE_VOLUME.hero;
let engineTimer = null;
let crossfadeFrame = null;
let sceneFadeFrame = null;
let crossfading = false;
let lastWhistleAt = Number.NEGATIVE_INFINITY;
let pendingWhistleTimer = null;
let finaleWhistlePlayed = false;
const oneShots = new Set();

function makeAudio(src) {
  const audio = new Audio(src);
  audio.preload = 'auto';
  audio.loop = false;
  audio.playsInline = true;
  audio.volume = 0;
  return audio;
}

function ensureDecks() {
  if (!decks) decks = [makeAudio(FILES.suite), makeAudio(FILES.suite)];
  return decks;
}

function applyDeckVolumes() {
  if (!decks) return;
  decks.forEach((deck, index) => {
    deck.volume = Math.max(0, Math.min(1, deckGain[index] * sceneVolume));
  });
}

function cancelFrame(frame) {
  if (frame) cancelAnimationFrame(frame);
}

function stopEngineTimer() {
  if (engineTimer) window.clearInterval(engineTimer);
  engineTimer = null;
}

function clearPendingWhistle() {
  if (pendingWhistleTimer) window.clearTimeout(pendingWhistleTimer);
  pendingWhistleTimer = null;
}

function stopOneShots() {
  oneShots.forEach((audio) => {
    audio.pause();
    audio.currentTime = 0;
  });
  oneShots.clear();
}

function playOneShot(file, volume) {
  if (!active || suspended) return false;
  const audio = makeAudio(file);
  audio.volume = volume;
  oneShots.add(audio);
  const cleanup = () => oneShots.delete(audio);
  audio.addEventListener('ended', cleanup, { once: true });
  audio.addEventListener('error', cleanup, { once: true });
  audio.play().catch(cleanup);
  return true;
}

function playWhistle(kind, expectedScene) {
  if (!active || suspended || (expectedScene && currentScene !== expectedScene)) return;
  const elapsed = performance.now() - lastWhistleAt;
  if (elapsed < WHISTLE_COOLDOWN_MS) {
    clearPendingWhistle();
    pendingWhistleTimer = window.setTimeout(
      () => playWhistle(kind, expectedScene),
      WHISTLE_COOLDOWN_MS - elapsed + 40,
    );
    return;
  }

  const cue = kind === 'finale'
    ? [FILES.whistleDistant, 0.43]
    : [FILES.whistleStrong, 0.38];
  if (playOneShot(...cue)) lastWhistleAt = performance.now();
}

function scheduleSceneWhistle(kind, scene, delay = 500) {
  clearPendingWhistle();
  pendingWhistleTimer = window.setTimeout(() => {
    pendingWhistleTimer = null;
    playWhistle(kind, scene);
  }, delay);
}

function fadeSceneVolume(target, duration = 700) {
  cancelFrame(sceneFadeFrame);
  const from = sceneVolume;
  const startedAt = performance.now();
  const tick = (now) => {
    const progress = Math.min(1, (now - startedAt) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    sceneVolume = from + (target - from) * eased;
    applyDeckVolumes();
    if (progress < 1) sceneFadeFrame = requestAnimationFrame(tick);
    else sceneFadeFrame = null;
  };
  sceneFadeFrame = requestAnimationFrame(tick);
}

function completeCrossfade(nextIndex) {
  const media = ensureDecks();
  const previousIndex = 1 - nextIndex;
  media[previousIndex].pause();
  media[previousIndex].currentTime = 0;
  activeDeckIndex = nextIndex;
  deckGain = nextIndex === 0 ? [1, 0] : [0, 1];
  crossfading = false;
  crossfadeFrame = null;
  applyDeckVolumes();
}

function beginCrossfade() {
  if (!active || suspended || crossfading) return;
  const media = ensureDecks();
  const previousIndex = activeDeckIndex;
  const nextIndex = 1 - previousIndex;
  const nextDeck = media[nextIndex];
  nextDeck.currentTime = 0;
  deckGain[nextIndex] = 0;
  applyDeckVolumes();
  crossfading = true;

  const playback = nextDeck.play();
  if (playback?.catch) {
    playback.catch(() => {
      crossfading = false;
      media[previousIndex].currentTime = 0;
      media[previousIndex].play().catch(() => {});
    });
  }

  const startedAt = performance.now();
  const tick = (now) => {
    if (!active || suspended || !crossfading) return;
    const progress = Math.min(1, (now - startedAt) / (CROSSFADE_SECONDS * 1000));
    // Equal-power curves keep perceived loudness steady through the overlap.
    deckGain[previousIndex] = Math.cos(progress * Math.PI / 2);
    deckGain[nextIndex] = Math.sin(progress * Math.PI / 2);
    applyDeckVolumes();
    if (progress < 1) crossfadeFrame = requestAnimationFrame(tick);
    else completeCrossfade(nextIndex);
  };
  crossfadeFrame = requestAnimationFrame(tick);
}

function startEngineTimer() {
  stopEngineTimer();
  engineTimer = window.setInterval(() => {
    if (!active || suspended || crossfading || !decks) return;
    if (decks[activeDeckIndex].currentTime >= CROSSFADE_AT) beginCrossfade();
  }, 60);
}

function resetDecks() {
  if (!decks) return;
  decks.forEach((deck) => {
    deck.pause();
    deck.currentTime = 0;
  });
  activeDeckIndex = 0;
  deckGain = [1, 0];
  crossfading = false;
  applyDeckVolumes();
}

async function startMediaNow() {
  const media = ensureDecks();
  suspended = false;
  cancelFrame(crossfadeFrame);
  resetDecks();
  sceneVolume = SCENE_VOLUME[currentScene] || SCENE_VOLUME.hero;
  applyDeckVolumes();

  // Start both elements in the same user-activation turn. Deck B is silenced,
  // immediately paused and kept unlocked for the later Safari crossfade.
  const primaryPromise = media[0].play();
  const reservePromise = media[1].play();
  try {
    await primaryPromise;
    if (reservePromise?.then) {
      reservePromise.then(() => {
        if (!crossfading && activeDeckIndex === 0) {
          media[1].pause();
          media[1].currentTime = 0;
        }
      }).catch(() => {});
    }
    active = true;
    // The suite itself contains the departure call at 0:01.45. Count it for
    // cooldown purposes so an early section change cannot stack another cue.
    lastWhistleAt = performance.now();
    startEngineTimer();
    if (currentScene === 'railway') scheduleSceneWhistle('railway', 'railway', 900);
    if (currentScene === 'finale' && !finaleWhistlePlayed) {
      finaleWhistlePlayed = true;
      scheduleSceneWhistle('finale', 'finale', 650);
    }
    return true;
  } catch {
    active = false;
    media.forEach((deck) => deck.pause());
    return false;
  }
}

export async function startRailwayAudio() {
  if (active) return true;
  if (starting) return starting;
  starting = startMediaNow().finally(() => { starting = null; });
  return starting;
}

// This direct path is called from click/tap/keydown so browser user activation
// is never swallowed by an earlier rejected autoplay promise.
export async function forceStartRailwayAudio() {
  if (active) return true;
  starting = null;
  return startMediaNow();
}

export function stopRailwayAudio() {
  active = false;
  suspended = false;
  stopEngineTimer();
  clearPendingWhistle();
  cancelFrame(crossfadeFrame);
  cancelFrame(sceneFadeFrame);
  crossfadeFrame = null;
  sceneFadeFrame = null;
  stopOneShots();
  resetDecks();
}

export function setRailwayAudioScene(scene) {
  if (!SCENE_VOLUME[scene] || scene === currentScene) return;
  currentScene = scene;
  if (!active) {
    sceneVolume = SCENE_VOLUME[scene];
    return;
  }
  fadeSceneVolume(SCENE_VOLUME[scene]);
  if (scene === 'railway') scheduleSceneWhistle('railway', 'railway', 700);
  if (scene === 'finale' && !finaleWhistlePlayed) {
    finaleWhistlePlayed = true;
    scheduleSceneWhistle('finale', 'finale', 450);
  }
}

export function suspendRailwayAudio() {
  if (!active || !decks || suspended) return;
  suspended = true;
  stopEngineTimer();
  clearPendingWhistle();
  stopOneShots();
  cancelFrame(crossfadeFrame);
  crossfadeFrame = null;

  if (crossfading) {
    const keep = deckGain[1] > deckGain[0] ? 1 : 0;
    const drop = 1 - keep;
    decks[drop].pause();
    decks[drop].currentTime = 0;
    activeDeckIndex = keep;
    deckGain = keep === 0 ? [1, 0] : [0, 1];
    crossfading = false;
  }
  decks.forEach((deck) => deck.pause());
  applyDeckVolumes();
}

export function resumeRailwayAudio() {
  if (!active || !decks || !suspended) return;
  suspended = false;
  decks[activeDeckIndex].play().then(startEngineTimer).catch(() => {});
}

export const startAmbience = startRailwayAudio;
export const stopAmbience = stopRailwayAudio;
